import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Box } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles'
import { makeStyles } from '@Compat/makeStyles';
import { siteTokens } from "../siteTokens";
import type { SiteTokens } from "../siteTokens";
import { useColorMode } from "../ColorModeContext";
import { futuristicThemes } from "@Components/theme/futuristic";
import { useInView } from "react-intersection-observer";
import Hero from "./sections/Hero";
import { MOUNT_ALL_SECTIONS_EVENT, sectionRegistry } from "../siteSections";
import { SHOW_FOUNDER } from "../founder";

// Only the hero ships in the marketing entry. Every other section is its own
// chunk, fetched and mounted as it nears the viewport (DeferredSection below)
// — PageSpeed's Total Blocking Time came almost entirely from parsing and
// running all fifteen sections up front.
const Impact = React.lazy(() => import(/* webpackChunkName: "site-impact" */ "./sections/Impact"));
const Problem = React.lazy(() => import(/* webpackChunkName: "site-problem" */ "./sections/Problem"));
const Sectors = React.lazy(() => import(/* webpackChunkName: "site-sectors" */ "./sections/Sectors"));
const Integrations = React.lazy(() => import(/* webpackChunkName: "site-integrations" */ "./sections/Integrations"));
const Countries = React.lazy(() => import(/* webpackChunkName: "site-countries" */ "./sections/Countries"));
const Sofia = React.lazy(() => import(/* webpackChunkName: "site-sofia" */ "./sections/Sofia"));
const Features = React.lazy(() => import(/* webpackChunkName: "site-features" */ "./sections/Features"));
const ContextDemo = React.lazy(() => import(/* webpackChunkName: "site-context-demo" */ "./sections/ContextDemo"));
const Testimonials = React.lazy(() => import(/* webpackChunkName: "site-testimonials" */ "./sections/Testimonials"));
const Faq = React.lazy(() => import(/* webpackChunkName: "site-faq" */ "./sections/Faq"));
const DemoJourney = React.lazy(() => import(/* webpackChunkName: "site-demo-journey" */ "./sections/DemoJourney"));
const Modules = React.lazy(() => import(/* webpackChunkName: "site-modules" */ "./sections/Modules"));
const Security = React.lazy(() => import(/* webpackChunkName: "site-security" */ "./sections/Security"));
const Founder = React.lazy(() => import(/* webpackChunkName: "site-founder" */ "./sections/Founder"));
const FinalCta = React.lazy(() => import(/* webpackChunkName: "site-final-cta" */ "./sections/FinalCta"));
const Roadmap = React.lazy(() => import(/* webpackChunkName: "site-roadmap" */ "./sections/Roadmap"));
import { ISections } from "../App";
import { useSiteContent } from "../SiteContentContext";
import Footer from "./Footer";
import MobileStickyCta from "./MobileStickyCta";
import { useCookieConsent } from "@Components/consent/CookieConsentProvider";
import { readLegalHash } from "@Components/legal/legalHash";

// Sections whose own inner panel is deliberately dark regardless of site
// theme (see each component's own styling comments). Used to detect when
// two of them land back to back so the divider band between them can be
// inserted — see `isDarkPanelBoundary` below.
const DARK_PANEL_COMPONENT_TYPES = new Set<unknown>([ContextDemo, Sofia, Testimonials]);

/** How far ahead of the viewport a deferred section starts loading. */
const DEFER_ROOT_MARGIN = "150% 0px";

/** Renders once the Suspense boundary above it resolved — i.e. the section's
 *  chunk loaded and the section itself committed. */
const RenderedMarker: React.FC<{ onRendered: () => void }> = ({ onRendered }) => {
  useEffect(() => { onRendered(); }, [onRendered]);
  return null;
};

/**
 * Holds a below-the-fold section back until it is near the viewport (or a
 * jump to some section asks for everything — see siteSections.ts). Until
 * then it is a viewport-tall placeholder, so the page keeps roughly its
 * final height and anything already on screen never shifts.
 */
const DeferredSection: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const registered = useRef(false);
  if (!registered.current) {
    registered.current = true;
    sectionRegistry.add();
  }
  const reported = useRef(false);
  const onRendered = React.useCallback(() => {
    if (reported.current) return;
    reported.current = true;
    sectionRegistry.rendered();
  }, []);

  useEffect(() => {
    if (mounted) return undefined;
    const mountNow = () => setMounted(true);
    window.addEventListener(MOUNT_ALL_SECTIONS_EVENT, mountNow);
    let observer: IntersectionObserver | undefined;
    if (typeof IntersectionObserver === "undefined") {
      mountNow();
    } else if (placeholderRef.current) {
      observer = new IntersectionObserver(
        (entries) => { if (entries.some((e) => e.isIntersecting)) mountNow(); },
        { rootMargin: DEFER_ROOT_MARGIN }
      );
      observer.observe(placeholderRef.current);
    }
    return () => {
      window.removeEventListener(MOUNT_ALL_SECTIONS_EVENT, mountNow);
      observer?.disconnect();
    };
  }, [mounted]);

  useEffect(() => () => {
    // Unmounted before rendering (route change): don't leave the registry waiting.
    if (!reported.current) {
      reported.current = true;
      sectionRegistry.rendered();
    }
  }, []);

  const placeholder = <div ref={placeholderRef} style={{ minHeight: "100vh", width: "100%" }} aria-hidden />;
  if (!mounted) return placeholder;
  return (
    <Suspense fallback={<div style={{ minHeight: "100vh", width: "100%" }} aria-hidden />}>
      {children}
      <RenderedMarker onRendered={onRendered} />
    </Suspense>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  const tk = siteTokens(theme);
  return {
  // Outer shell — fills the viewport. The section gradient is set inline so
  // it transitions on activeIndex.
  container: {
    position: "relative",
    width: "100%",
    // Shrinks by the cookie banner's real height while it's shown (set as
    // a CSS custom property on this element by VerticalScroller — see its
    // `--consent-banner-offset` inline style) so the fixed-position banner
    // can never land on top of a CTA in the last slice of the viewport.
    // Defaults to 0px, i.e. plain 100vh, once the property is unset.
    height: "calc(100vh - var(--consent-banner-offset, 0px))",
    transition: "background 600ms ease",
    // Every section anchor (Tour, Pricing, FAQ, …) gets a top margin when
    // scrolled into view, so the fixed AppBar doesn't overlap the section's
    // title. Applied here via descendant selector so it covers every section
    // component without each one needing its own style.
    "& [data-site-section]": {
      scrollMarginTop: theme.spacing(10)
    },
    // Accessibility: collapse every CSS animation + transition + smooth
    // scroll when the visitor has `prefers-reduced-motion: reduce`. Scoped to
    // the marketing-site container so it doesn't bleed into the app.
    "@media (prefers-reduced-motion: reduce)": {
      "&, & *, & *::before, & *::after": {
        animationDuration: "0.01ms !important",
        animationIterationCount: "1 !important",
        transitionDuration: "0.01ms !important",
        scrollBehavior: "auto !important"
      }
    }
  },
  // Native page-level scroll container (`data-site-scroller`: the header and
  // the sticky CTA listen to its scroll, not the window's). The scrollbar is
  // hidden so the hero edge doesn't sit next to a gutter stripe; scroll still
  // works via wheel / touch / keyboard.
  scroll: {
    width: "100%",
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
      width: 0,
      height: 0
    }
  },
  // Each section is a full-width band with its own gradient backdrop. The
  // gradient runs edge-to-edge while the content inside is held in a centered
  // max-width container (sectionInner), so wide monitors don't get a
  // left-justified narrow column with empty right gutter.
  section: {
    width: "100%",
    // Each section is its own "screen moment" — the visitor lands in a
    // section, takes it in, then scrolls deliberately. Combined with the
    // internal section padding and the alternating gradient backdrop, this
    // gives the page proper rhythm. Same cookie-banner reservation as
    // `.container` above — inherits the custom property from there, and
    // the <900px override below still wins regardless of its value since
    // it sets a plain "auto", not a var()-based expression.
    minHeight: "calc(100vh - var(--consent-banner-offset, 0px))",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    // CRITICAL on desktop: these sections are flex children of the column
    // `scroll` container. With the default flex-shrink:1, any section whose
    // content is TALLER than the viewport (e.g. the hero with its big H1 +
    // chips + product mockup, or the Tour section) gets compressed back
    // toward the 100vh floor — and `justify-content:center` then pushes the
    // overflow out BOTH top and bottom, bleeding a tall section's heading up
    // over its neighbour above and clipping the other edge. flex-shrink:0
    // lets a tall section keep its real height so its centered content fits
    // without overflowing into the neighbour above.
    flexShrink: 0,
    scrollMarginTop: theme.spacing(10),
    // At <=899px a section's stacked content (grids collapse to one column)
    // routinely exceeds the viewport. Forcing min-height:100vh + vertical
    // centering then either strands short sections in a tall empty band or
    // glues tall ones to the top edge with an arbitrary offset. Below the
    // tablet breakpoint, let the content dictate height and give every section
    // consistent vertical padding for rhythm instead. Raw @media so the rule
    // lands regardless of how makeStyles resolves theme.breakpoints.
    "@media (max-width: 899.95px)": {
      minHeight: "auto",
      justifyContent: "flex-start",
      paddingTop: theme.spacing(8),
      paddingBottom: theme.spacing(8)
    }
  },
  // Variant of `.section` applied to a section immediately followed by
  // another dark-paneled section (see `DARK_PANEL_COMPONENT_TYPES`). Its
  // marginBottom is suppressed because the `.sectionDivider` element
  // rendered immediately below it owns the visual break instead.
  sectionWithDivider: {
    marginBottom: 0
  },
  sectionInner: {
    width: "100%",
    maxWidth: 1200,
    margin: "0 auto",
    padding: theme.spacing(0, 4),
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 2)
    }
  },
  footer: {
    width: "100%"
  },
  // Solid-black divider rendered between two consecutive dark-paneled
  // sections at <md (see `DARK_PANEL_COMPONENT_TYPES` /
  // `isDarkPanelBoundary`). Both land on dark gradient stops in dark
  // mode, so even with section margins the boundary read as "one
  // continuous dark block". A scoped deepest-tone band breaks the optical merge
  // for that pair only — every other section pair keeps the gradient
  // transition.
  sectionDivider: {
    width: "100%",
    height: 0,
    background: tk.surfaceDeep,
    transition: "background 200ms ease",
    // Without this, the flexbox shrink algorithm crushes this div toward
    // 0 regardless of the height set below it — `.scroll`'s children sum
    // to many times its own height, and every child with the default
    // flex-shrink:1 absorbs some of that negative space proportionally.
    // `.section` above already sets this for exactly the same reason (see
    // its own comment); this div was missed when it was first added.
    flexShrink: 0,
    [theme.breakpoints.down("md")]: {
      height: theme.spacing(6)
    },
    [theme.breakpoints.down("sm")]: {
      height: theme.spacing(4)
    }
  }
  };
});

// Section backgrounds alternate between two tones of the selected theme
// (page / slightly recessed surface) so the page still reads as distinct
// "chapters" without a different hue per section.
//
// Order matches the prototype's narrative sequence (Hero → Problem (pain)
// → Tour → "noise to context" → Sofia → outcome/proof → integrations →
// …→ pricing → next-steps → FAQ, last before the footer) — see the
// `sections` useMemo below for the full rationale per section. Three
// sections carry their own dark inner panel in dark mode only
// (ContextDemo, Sofia, Testimonials) — in light mode they're plain
// cards like every other section, so their wash is just the ordinary
// alternating tone, same as their neighbors.
//
// Futuristic themes paint a gradient + neon grid on the body (CssBaseline);
// their section washes stay transparent / faintly glassy so that backdrop
// shows through, the same as on the app's screens.
const TESTIMONIALS_STOP_INDEX = 7;
const SECTION_STOP_COUNT = 18;

const buildSectionStops = (tk: SiteTokens, futuristic: boolean): string[] =>
  Array.from({ length: SECTION_STOP_COUNT }, (_, i) => {
    if (futuristic) return i % 2 === 0 ? "transparent" : tk.pageA(0.35);
    // Testimonials sits on the deepest tone in dark mode.
    if (tk.dark && i === TESTIMONIALS_STOP_INDEX) return tk.surfaceDeep;
    return i % 2 === 0 ? tk.surfaceSoft : tk.page;
  });

const VerticalScroller: React.FC = () => {
  const { content } = useSiteContent();
  const [activeIndex, setActiveIndex] = useState(0);
  const classes = useStyles();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // The cookie banner is `position: fixed` at the bottom of the viewport
  // with no reserved space of its own — on load, before any scrolling,
  // it can land directly on top of the hero's CTA (which sits in the
  // last ~200px of a typical phone viewport). Shrinking every full-height
  // section by the banner's real height while it's visible keeps centered
  // content clear of it; this reverts to plain 100vh the instant the
  // visitor accepts/rejects/customizes.
  const { bannerHeight } = useCookieConsent();

  // Force the page to land at the top on first mount. Two things can push the
  // initial scroll position off zero:
  //   1. The browser's auto scroll-restoration on reload.
  //   2. A URL hash (e.g. `#features`) auto-scrolling to a section.
  // Without this, the hero H1 was rendering above the visible viewport —
  // visitors arrived on the subhead, never seeing the headline.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // Clear any hash so the browser doesn't auto-scroll to a section (or to
    // an empty `#` anchor that lands mid-page). The legal hashes (#policy,
    // #terms, #cookies) name no element, so they can't scroll anything —
    // and LegalDeepLink still has to read them after this effect runs.
    if (window.location.hash && !readLegalHash()) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    const reset = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
        scrollRef.current.scrollLeft = 0;
      }
      window.scrollTo(0, 0);
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
    };
    reset();
    // Retry at increasing delays — covers font-loading reflows and
    // image-decode reflows that can nudge the scroll position.
    const handles = [50, 150, 300, 600].map((ms) => window.setTimeout(reset, ms));
    return () => { handles.forEach(window.clearTimeout); };
  }, []);

  const sections = useMemo<ISections[]>(() => {
    // Order matches the new marketing prototype's narrative sequence:
    // hero (hook) → problem (pain) → tour/"areas" (what replaces it) →
    // noise-to-context demo → Sofia (AI, while still warm) → outcome/proof
    // → testimonials (wall) → integrations (trust) → …→ pricing →
    // next-steps ("what happens after you book") → FAQ last, right before
    // the footer CTA. Sections with no prototype equivalent (Roadmap,
    // BecomePartner, Security) are slotted into the gap between Integrations
    // and Pricing, in the order they already had. Modules (the by-plan
    // feature breakdown) sits right after Pricing instead — a visitor
    // drills into "what's actually included" once they've already seen
    // the price, not before.
    const list: ISections[] = [
      {
        name: content.hero.title,
        component: <Hero />
      }
    ];

    if ((content as { painPoints?: { title?: string } }).painPoints?.title) {
      list.push({
        name: (content as { painPoints: { title: string } }).painPoints.title,
        component: <Problem />
      });
    }

    // Sectors right after the pain: "is this for a business like mine?",
    // answered with photos of people at work before the product tour.
    // Optional block — a locale that hasn't authored `sectors` omits it.
    if ((content as { sectors?: { items?: unknown[] } }).sectors?.items?.length) {
      list.push({
        name: (content as { sectors: { title: string } }).sectors.title,
        component: <Sectors />
      });
    }

    if (content.features?.groups?.length) {
      list.push({
        name: content.features.title,
        component: <Features />
      });
    }

    // "Do ruído ao contexto" ON/OFF demo — right after the tour, while the
    // visitor is still warm from seeing real product screens. Optional
    // block, guarded the same way as the other locale-gated sections.
    if ((content as { contextDemo?: { windows?: unknown[] } }).contextDemo?.windows?.length) {
      list.push({
        name: (content as { contextDemo: { title: string } }).contextDemo.title,
        component: <ContextDemo />
      });
    }

    // Sofia lands right after the "noise to context" demo — the standout AI
    // differentiator while the visitor is still warm from the product demo.
    // Guarded so a locale that hasn't authored the `sofia` block yet simply
    // omits the section.
    if ((content as { sofia?: { title?: string } }).sofia) {
      list.push({
        name: (content as { sofia: { title: string } }).sofia.title,
        component: <Sofia />
      });
    }

    if (content.impact?.stats?.length) {
      list.push({
        name: content.impact.title,
        component: <Impact />
      });
    }

    if (content.testimonials?.quotes?.length) {
      list.push({
        name: content.testimonials.title,
        component: <Testimonials />
      });
    }

    // Integrations sit right after the proof/outcome beats: the visitor has
    // just been shown why Fidava is worth switching to, so the next
    // question is "does it talk to what I already pay for?". Optional
    // block — a locale that hasn't authored `integrations` simply omits it.
    if ((content as { integrations?: { title?: string } }).integrations) {
      list.push({
        name: (content as { integrations: { title: string } }).integrations.title,
        component: <Integrations />
      });
    }

    // Invoicing + payroll coverage per country, right after Integrations:
    // the board above shows the providers as part of the wider stack; this
    // answers the follow-up a visitor outside Portugal has ("and in my
    // country?") while the integration context is still on screen, and
    // before the forward-looking Roadmap. Optional block — a locale that
    // hasn't authored `countries` simply omits it.
    if ((content as { countries?: { title?: string } }).countries?.title) {
      list.push({
        name: (content as { countries: { title: string } }).countries.title,
        component: <Countries />
      });
    }

    if (content.roadmap?.milestones?.length) {
      list.push({
        name: content.roadmap.title,
        component: <Roadmap />
      });
    }

    // Trust/security reassurance, right before the pricing ask — the moment a
    // visitor evaluating a newer product is most likely to hesitate over
    // "can I trust them with my data?". Optional block — omitted locales just
    // skip it.
    if ((content as { security?: { title?: string } }).security) {
      list.push({
        name: (content as { security: { title: string } }).security.title,
        component: <Security />
      });
    }

    // The founder's note, between the security reassurance and the pricing
    // ask: after "can I trust the product?" comes "who's behind it, and who
    // answers me?". Optional block — omitted locales just skip it.
    if (SHOW_FOUNDER && (content as { founder?: { title?: string } }).founder?.title) {
      list.push({
        name: (content as { founder: { title: string } }).founder.title,
        component: <Founder />
      });
    }

    if (content.finalCta) {
      list.push({
        name: content.finalCta.title,
        component: <FinalCta />
      });
    }

    // Modules-by-plan breakdown. Sits right after pricing, letting a visitor
    // who's already seen the price drill into exactly what each plan
    // unlocks. Optional block — omitted locales just skip it.
    if ((content as { modules?: { plans?: unknown[] } }).modules?.plans?.length) {
      list.push({
        name: (content as { modules: { title: string } }).modules.title,
        component: <Modules />
      });
    }

    // "What happens in the demo" 3-step — right after pricing, answering
    // "what do I actually get" as reassurance immediately after the ask,
    // matching the prototype's own Pricing → Next-steps → FAQ sequence.
    // Optional block — omitted locales just skip it.
    if ((content as { demoJourney?: { steps?: unknown[] } }).demoJourney?.steps?.length) {
      list.push({
        name: (content as { demoJourney: { title: string } }).demoJourney.title,
        component: <DemoJourney />
      });
    }

    // FAQ, right after pricing/next-steps, same as the prototype's own
    // closing sequence.
    if (content.faq?.items?.length) {
      list.push({
        name: content.faq.title,
        component: <Faq />
      });
    }

    return list;
  }, [content]);

  const theme = useTheme();
  const themeMode = theme.palette.mode;
  const { themeName } = useColorMode();
  const futuristic = Boolean(futuristicThemes[themeName]);
  const sectionStops = useMemo(() => {
    const desiredLength = sections.length + 1;
    const customStops = content.theme?.sectionStops;
    const baseStops = Array.isArray(customStops) && customStops.length >= 2
      ? customStops
      : buildSectionStops(siteTokens(theme), futuristic);

    if (baseStops.length >= desiredLength) {
      return baseStops.slice(0, desiredLength);
    }

    const extended = [...baseStops];
    while (extended.length < desiredLength) {
      const nextIndex = extended.length % baseStops.length;
      extended.push(baseStops[nextIndex]);
    }
    return extended;
  }, [content.theme?.sectionStops, sections.length, theme, futuristic]);

  const activeStartColor = sectionStops[activeIndex] ?? sectionStops[0];
  const activeEndColor = sectionStops[activeIndex + 1] ?? sectionStops[sectionStops.length - 1] ?? sectionStops[0];
  const containerBackground = useMemo(
    // Futuristic: let the body's gradient + grid show behind the sections.
    () => futuristic
      ? "transparent"
      : `linear-gradient(180deg, ${activeStartColor} 0%, ${activeEndColor} 100%)`,
    [activeStartColor, activeEndColor, futuristic]
  );

  return (
    <Box
      className={classes.container}
      style={{
        background: containerBackground,
        // CSS custom property, not a direct `height`/`minHeight` inline
        // override — it must inherit down through `.section` without
        // beating the `<900px` media query that sets minHeight:"auto"
        // there (forcing a fixed height on mobile is the exact class of
        // bug documented on `.section` below; see its own comment).
        ["--consent-banner-offset" as any]: bannerHeight > 0 ? `${bannerHeight}px` : "0px"
      }}
    >
      <MobileStickyCta />
      <div ref={scrollRef} className={classes.scroll} data-site-scroller>
        {sections.map((Section, index) => {
          // Per-iteration `useInView` is intentional — the section list
          // is stable across renders, so the hook order is stable too.
          // Each section reports its own visibility to drive the
          // container's gradient transition.
          const { ref, inView } = useInView({
            threshold: 0.3,
            triggerOnce: false
          }) as any;
          const startColor = sectionStops[index] ?? sectionStops[0];
          const endColor = sectionStops[index + 1] ?? sectionStops[index] ?? sectionStops[0];
          const background = `linear-gradient(180deg, ${startColor} 0%, ${endColor} 100%)`;

          useEffect(() => {
            if (inView) {
              setActiveIndex(index);
            }
          }, [inView]);

          // Three sections carry their own dark inner panel in dark mode
          // (ContextDemo, Sofia, Testimonials — see each component's own
          // styling; in light mode they're plain white cards like every
          // other section). When two of them land back to back (currently
          // ContextDemo → Sofia) AND the site is in dark mode, both wash
          // stops are dark and the panels read as one continuous block. A
          // black divider band is inserted between such a pair at <md;
          // every other pair — and this same pair in light mode — stays on
          // the natural gradient transition.
          const isDarkPanelBoundary =
            themeMode === "dark" &&
            DARK_PANEL_COMPONENT_TYPES.has(Section.component.type) &&
            DARK_PANEL_COMPONENT_TYPES.has(sections[index + 1]?.component?.type);

          return (
            <React.Fragment key={index}>
              <div
                ref={ref}
                id={`section-${index}`}
                className={`${classes.section}${isDarkPanelBoundary ? ` ${classes.sectionWithDivider}` : ""}`}
                style={{ background }}
              >
                <div className={classes.sectionInner}>
                  {index === 0 ? Section.component : <DeferredSection>{Section.component}</DeferredSection>}
                </div>
              </div>
              {isDarkPanelBoundary ? (
                <div className={classes.sectionDivider} aria-hidden />
              ) : null}
            </React.Fragment>
          );
        })}
        <div
          className={classes.footer}
          style={{
            background: `linear-gradient(180deg, ${sectionStops[sections.length] ?? sectionStops[0]} 0%, ${sectionStops[sections.length] ?? sectionStops[0]} 100%)`
          }}
        >
          <Footer />
        </div>
      </div>
    </Box>
  );
};

export default VerticalScroller;
